import { Controller } from "stimulus"

// #1971: het formulier voor een roosteractiviteit (appointments/_form), naast
// rooster-formulier (eindtijd volgt het type, +1 dag). Deze controller:
//   - rekent de duur uit zodra begin- of eindtijd verandert;
//   - toont de reeksinstellingen alleen bij Terugkerende reeks;
//   - houdt de weekdag van de datum altijd gekozen (en verschuift die mee);
//   - werkt de samenvatting bij, met het aantal activiteiten van een reeks;
//   - vraagt de server (appointments#controle) of er iets botst, zonder op
//     te slaan, en toont dat als waarschuwing;
//   - voorkomt dubbel opslaan en waarschuwt bij Annuleren met wijzigingen.
// Ronde 15: Dagelijks of Wekelijks met "Herhaal iedere n week", online boeken
// als schakelaar die het type volgt tot je hem zelf omzet, en de sluitknop
// van het venster (appointments/_venster) doet hetzelfde als Annuleren.
// Zonder JavaScript doet het formulier hetzelfde, alleen niet live.
export default class extends Controller {
  static targets = ["type", "trainer", "locatie", "maximum", "datum", "begin", "eind", "duur", "controle",
                    "soort", "reeks", "frequentie", "wekelijks", "dagelijksHulp", "interval", "weekOp", "weekdag",
                    "einde", "aantal", "einddatum",
                    "svNaam", "svWanneer", "svTijd", "svMaximum", "svLocatie", "svTrainer", "svAantal",
                    "online", "knop"]
  static values = { controleUrl: String, id: Number, taal: String, maximum: Number, teksten: Object }

  connect() {
    this.startdag = this.datumWaarde()?.getDay()
    this.controleSleutel = null
    this.bezig = false
    this.onlineEigen = this.hasOnlineTarget && this.onlineTarget.dataset.eigen === "1"
    this.soortGekozen()
    this.bijwerken()
    this.beginstand = this.stand()
    // De sluitknop van het venster staat buiten het formulier
    this.sluitknop = this.element.closest("[data-ra-venster]")?.querySelector("[data-ra-sluiten]")
    this.sluiten = (event) => this.annuleren(event)
    this.sluitknop?.addEventListener("click", this.sluiten)
  }

  disconnect() {
    clearTimeout(this.controleTimer)
    this.sluitknop?.removeEventListener("click", this.sluiten)
  }

  // ---- bij elke wijziging ----

  bijwerken() {
    this.volgStartdag()
    this.toonFrequentie()
    this.volgOnline()
    this.toonDuur()
    this.toonSamenvatting()
    this.planControle()
  }

  typeGekozen() {
    const optie = this.typeTarget.selectedOptions[0]
    this.volgOnline()
    // De lege keuze van de afzegtermijn noemt de termijn van het type
    const afzeggen = this.element.querySelector("[data-roosteractiviteit-target='afzeggen'] option[value='']")
    if (afzeggen && optie?.dataset.afzeggen) afzeggen.textContent = optie.dataset.afzeggen
  }

  soortGekozen() {
    if (!this.hasReeksTarget) return
    this.reeksTarget.hidden = !this.herhalen()
  }

  dagGekozen(event) {
    // De dag van de datum blijft gekozen: daar begint de reeks
    const vak = event.target
    if (Number(vak.value) === this.startdag && !vak.checked) vak.checked = true
  }

  minder() {
    this.zetMaximum(-1)
  }

  // Wie de schakelaar zelf omzet, houdt zijn keuze; anders volgt hij het type
  onlineGekozen() {
    this.onlineEigen = true
  }

  // Een klik in het aantal of de einddatum kiest die manier van eindigen
  kiesEinde(event) {
    const knop = event.target.closest(".ra-einde-keuze")?.querySelector("input[type=radio]")
    if (knop && !knop.checked) {
      knop.checked = true
      this.bijwerken()
    }
  }

  meer() {
    this.zetMaximum(1)
  }

  // ---- opslaan en annuleren ----

  versturen(event) {
    if (this.bezig) {
      event.preventDefault()
      return
    }
    this.bezig = true
    this.beginstand = this.stand()
    // Na het versturen, zodat de naam en waarde van de knop (Toch inplannen) meegaan
    setTimeout(() => this.knopTargets.forEach((knop) => {
      knop.classList.add("is-bezig")
      knop.setAttribute("aria-disabled", "true")
    }), 0)
  }

  klaar() {
    this.bezig = false
    this.knopTargets.forEach((knop) => {
      knop.classList.remove("is-bezig")
      knop.removeAttribute("aria-disabled")
    })
  }

  annuleren(event) {
    if (this.stand() === this.beginstand) return
    if (!window.confirm(this.teksten.wijzigingen_weg)) event.preventDefault()
  }

  // ---- onderdelen ----

  get teksten() {
    return this.tekstenValue || {}
  }

  dagelijks() {
    return this.hasFrequentieTarget && this.frequentieTarget.value === "dagelijks"
  }

  // Wekelijks: interval en weekdagen; Dagelijks: alle dagen, alleen een uitleg
  toonFrequentie() {
    const dagelijks = this.dagelijks()
    if (this.hasWekelijksTarget) this.wekelijksTarget.hidden = dagelijks
    if (this.hasDagelijksHulpTarget) this.dagelijksHulpTarget.hidden = !dagelijks
    if (this.hasWeekOpTarget) this.weekOpTarget.textContent = this.meervoud(this.teksten.week, this.interval())
  }

  interval() {
    if (this.dagelijks() || !this.hasIntervalTarget) return 1
    const waarde = parseInt(this.intervalTarget.value, 10) || 1
    return Math.min(Math.max(waarde, 1), 4)
  }

  // Zoals Appointment#online_boeken_standaard?: een type dat sporters zelf
  // boeken, of een groepsles; nooit bij een intern of uitgezet type
  volgOnline() {
    if (!this.hasOnlineTarget || !this.hasTypeTarget) return
    const optie = this.typeTarget.selectedOptions[0]
    const intern = !optie || optie.dataset.intern === "1"
    this.onlineTarget.disabled = intern
    if (intern) {
      this.onlineTarget.checked = false
      return
    }
    if (this.onlineEigen) return
    this.onlineTarget.checked = optie.dataset.online === "1" || (this.maximum() || 0) > 1
  }

  herhalen() {
    const gekozen = this.soortTargets.find((knop) => knop.checked)
    return !!gekozen && gekozen.value === "reeks"
  }

  volgStartdag() {
    if (!this.hasWeekdagTarget || this.dagelijks()) return
    const dag = this.datumWaarde()?.getDay()
    if (dag === undefined || dag === this.startdag) return
    this.weekdagTargets.forEach((vak) => {
      const waarde = Number(vak.value)
      if (waarde === this.startdag) vak.checked = false
      if (waarde === dag) vak.checked = true
      vak.closest(".ra-dag")?.classList.toggle("is-startdag", waarde === dag)
    })
    this.startdag = dag
  }

  toonDuur() {
    if (!this.hasDuurTarget) return
    const duur = this.duurMinuten()
    this.duurTarget.textContent = duur ? this.meervoud(this.teksten.duur, duur) : this.teksten.duur?.leeg || ""
  }

  toonSamenvatting() {
    if (this.hasSvNaamTarget) {
      const optie = this.typeTarget.selectedOptions[0]
      this.svNaamTarget.textContent = optie ? optie.textContent.trim() : this.teksten.leeg
    }
    if (this.hasSvWanneerTarget) this.svWanneerTarget.textContent = this.wanneer()
    if (this.hasSvTijdTarget) {
      const begin = this.beginTarget.value
      const eind = this.eindTarget.value
      const duur = this.duurMinuten()
      let tekst = [begin, eind].filter(Boolean).join(" – ")
      if (duur) tekst += ` (${this.meervoud(this.teksten.duur, duur)})`
      this.svTijdTarget.textContent = tekst
    }
    if (this.hasSvMaximumTarget) {
      const maximum = this.maximum()
      this.svMaximumTarget.textContent = maximum ? this.meervoud(this.teksten.maximum, maximum) : this.teksten.maximum?.geen
    }
    if (this.hasSvLocatieTarget && this.hasLocatieTarget) {
      this.svLocatieTarget.textContent = this.locatieTarget.value ? this.keuzeTekst(this.locatieTarget) : this.teksten.geen_locatie
    }
    if (this.hasSvTrainerTarget && this.hasTrainerTarget) {
      const trainer = this.trainerTarget
      if (trainer.tagName === "SELECT") {
        this.svTrainerTarget.textContent = trainer.value ? this.keuzeTekst(trainer) : this.teksten.geen_trainer
      } else {
        this.svTrainerTarget.textContent = trainer.textContent.trim()
      }
    }
    if (this.hasSvAantalTarget) {
      const aantal = this.herhalen() ? this.aantalActiviteiten() : null
      this.svAantalTarget.hidden = !aantal
      if (aantal) {
        let tekst = this.meervoud(this.teksten.aantal, aantal)
        const laatste = this.laatsteDatum()
        if (laatste) tekst += `, ${this.teksten.tot.replace("%{datum}", this.datumLang(laatste, false))}`
        this.svAantalTarget.textContent = tekst
      }
    }
  }

  // "dinsdag 3 november 2026", of bij een reeks "Wekelijks op dinsdag en donderdag"
  wanneer() {
    const datum = this.datumWaarde()
    if (!datum) return ""
    if (!this.herhalen()) return this.datumLang(datum)
    if (this.dagelijks()) return this.teksten.dagelijks

    const dagen = this.gekozenDagen().map((dag) => this.teksten.weekdagen_lang[dag])
    const lijst = dagen.length > 1 ? `${dagen.slice(0, -1).join(", ")} ${this.teksten.en} ${dagen[dagen.length - 1]}` : dagen[0]
    return `${this.teksten.interval[String(this.interval())] || ""} ${this.teksten.op} ${lijst}`
  }

  // Zoals de server: per gekozen weekdag een reeks, de dag van de datum eerst
  begindata() {
    const datum = this.datumWaarde()
    if (!datum) return []
    return this.gekozenDagen().map((dag) => {
      const begin = new Date(datum)
      begin.setDate(datum.getDate() + ((dag - datum.getDay() + 7) % 7))
      return begin
    })
  }

  // Ma tot en met zo, beginnend bij de volgorde van de week (voor de tekst)
  gekozenDagen() {
    if (this.dagelijks()) return [1, 2, 3, 4, 5, 6, 0]
    const dagen = this.weekdagTargets.filter((vak) => vak.checked).map((vak) => Number(vak.value))
    if (this.startdag !== undefined && !dagen.includes(this.startdag)) dagen.push(this.startdag)
    return dagen.sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7))
  }

  datumsVan(begin) {
    const stap = this.interval() * 7
    const maximum = this.maximumValue || 52
    const einde = this.eindeTargets.find((knop) => knop.checked)?.value || "aantal"
    const lijst = []
    if (einde === "datum") {
      const tot = this.leesDatum(this.einddatumTarget.value)
      if (!tot) return null
      for (let index = 0; index <= maximum; index++) {
        const datum = new Date(begin)
        datum.setDate(begin.getDate() + index * stap)
        if (datum > tot) break
        lijst.push(datum)
      }
    } else {
      const aantal = parseInt(this.aantalTarget.value, 10)
      if (!aantal || aantal < 1 || aantal > maximum) return null
      for (let index = 0; index < aantal; index++) {
        const datum = new Date(begin)
        datum.setDate(begin.getDate() + index * stap)
        lijst.push(datum)
      }
    }
    return lijst
  }

  aantalActiviteiten() {
    if (!this.hasReeksTarget) return null
    const reeksen = this.begindata().map((begin) => this.datumsVan(begin))
    if (reeksen.length === 0 || reeksen.some((lijst) => lijst === null)) return null
    return reeksen.reduce((som, lijst) => som + lijst.length, 0) || null
  }

  laatsteDatum() {
    if (!this.hasReeksTarget) return null
    const reeksen = this.begindata().map((begin) => this.datumsVan(begin))
    if (reeksen.some((lijst) => !lijst || lijst.length === 0)) return null
    const laatste = reeksen.map((lijst) => lijst[lijst.length - 1])
    return laatste.length ? new Date(Math.max(...laatste)) : null
  }

  maximum() {
    if (!this.hasMaximumTarget) return null
    const eigen = parseInt(this.maximumTarget.value, 10)
    if (eigen > 0) return eigen
    const optie = this.typeTarget.selectedOptions[0]
    return optie ? parseInt(optie.dataset.maximum, 10) || null : null
  }

  zetMaximum(stap) {
    const veld = this.maximumTarget
    const huidig = parseInt(veld.value, 10) || parseInt(veld.placeholder, 10) || (stap > 0 ? 0 : 1)
    const bovengrens = parseInt(veld.max, 10) || 999
    veld.value = Math.min(Math.max(huidig + stap, 1), bovengrens)
    veld.dispatchEvent(new Event("input", { bubbles: true }))
  }

  // ---- controle op botsingen (appointments#controle) ----

  planControle() {
    if (!this.hasControleTarget || !this.controleUrlValue) return
    const parameters = this.controleParameters()
    const sleutel = parameters.toString()
    if (sleutel === this.controleSleutel) return
    this.controleSleutel = sleutel
    clearTimeout(this.controleTimer)
    this.controleTimer = setTimeout(() => this.controleer(parameters, sleutel), 400)
  }

  controleParameters() {
    const gegevens = new FormData(this.element)
    const parameters = new URLSearchParams()
    const velden = ["appointment_type_id", "trainer_id", "location_id", "room", "datum", "begintijd", "eindtijd", "max_participants"]
    velden.forEach((veld) => {
      const waarde = gegevens.get(`appointment[${veld}]`)
      if (waarde !== null) parameters.set(`appointment[${veld}]`, waarde)
    })
    gegevens.getAll("appointment[deelnemer_ids][]").filter(Boolean).forEach((id) => parameters.append("appointment[deelnemer_ids][]", id))
    if (this.idValue) parameters.set("id", this.idValue)
    return parameters
  }

  async controleer(parameters, sleutel) {
    if (!parameters.get("appointment[datum]") || !parameters.get("appointment[begintijd]")) {
      this.toonControle({ vast: [], let: [] })
      return
    }
    const url = `${this.controleUrlValue}${this.controleUrlValue.includes("?") ? "&" : "?"}${parameters}`
    try {
      const antwoord = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      if (!antwoord.ok || sleutel !== this.controleSleutel) return
      this.toonControle(await antwoord.json())
    } catch (_fout) {
      // Geen verbinding: bij opslaan controleert de server alsnog
    }
  }

  toonControle(uitkomst) {
    const vak = this.controleTarget
    const vast = uitkomst.vast || []
    const let_ = uitkomst.let || []
    vak.replaceChildren()
    if (vast.length === 0 && let_.length === 0) {
      vak.hidden = true
      return
    }
    const titel = document.createElement("strong")
    titel.textContent = vast.length ? vak.dataset.titelVast : vak.dataset.titelLet
    const lijst = document.createElement("ul")
    vast.concat(let_).forEach((melding) => {
      const regel = document.createElement("li")
      regel.textContent = melding
      lijst.append(regel)
    })
    const hulp = document.createElement("p")
    hulp.textContent = vast.length ? vak.dataset.hulpVast : vak.dataset.hulpLet
    vak.append(titel, lijst, hulp)
    if (this.herhalen()) {
      const reeks = document.createElement("p")
      reeks.textContent = vak.dataset.hulpReeks
      vak.append(reeks)
    }
    vak.classList.toggle("is-vast", vast.length > 0)
    vak.hidden = false
  }

  // ---- hulpjes ----

  // Alles wat het formulier verstuurt, om te zien of er iets veranderd is
  stand() {
    const gegevens = new FormData(this.element)
    gegevens.delete("authenticity_token")
    return new URLSearchParams(gegevens).toString()
  }

  duurMinuten() {
    const begin = this.minuten(this.beginTarget.value)
    const eind = this.minuten(this.eindTarget.value)
    if (begin === null || eind === null || eind === begin) return null
    return eind > begin ? eind - begin : eind + 24 * 60 - begin
  }

  minuten(waarde) {
    const delen = /^(\d{1,2}):(\d{2})/.exec(waarde || "")
    return delen ? parseInt(delen[1], 10) * 60 + parseInt(delen[2], 10) : null
  }

  datumWaarde() {
    return this.hasDatumTarget ? this.leesDatum(this.datumTarget.value) : null
  }

  leesDatum(waarde) {
    const delen = /^(\d{4})-(\d{2})-(\d{2})$/.exec(waarde || "")
    return delen ? new Date(Number(delen[1]), Number(delen[2]) - 1, Number(delen[3])) : null
  }

  datumLang(datum, metDag = true) {
    const tekst = `${datum.getDate()} ${this.teksten.maanden[datum.getMonth()]} ${datum.getFullYear()}`
    return metDag ? `${this.teksten.weekdagen_lang[datum.getDay()]} ${tekst}` : tekst
  }

  keuzeTekst(select) {
    return select.selectedOptions[0]?.textContent.trim() || ""
  }

  meervoud(vormen, aantal) {
    if (!vormen) return String(aantal)
    return (aantal === 1 ? vormen.one : vormen.other).replace("%{count}", aantal)
  }
}
